export type RoasDecisionInput = Readonly<{
  adSpend: number;
  revenue: number;
  marginRate?: number;
  targetRoas?: number;
}>;

export type RoasDecisionResult = Readonly<{
  adSpend: number;
  revenue: number;
  roas: number | null;
  revenuePerWon: number | null;
  adCostRate: number | null;
  marginRate: number | null;
  targetRoas: number | null;
  estimatedProfitAfterAd: number | null;
  breakEvenRoas: number | null;
  maxAdSpendBeforeLoss: number | null;
  targetRevenue: number | null;
  maxAdSpendAtTarget: number | null;
  revenueGapToTarget: number | null;
  adSpendHeadroomToTarget: number | null;
}>;

export type RoasScenario = Readonly<{
  id: string;
  label: string;
  description: string;
  result: RoasDecisionResult;
}>;

function optionalFinite(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function calculateRoasDecision(input: RoasDecisionInput): RoasDecisionResult {
  const marginCandidate = optionalFinite(input.marginRate);
  const marginRate = marginCandidate !== null && marginCandidate > 0 ? marginCandidate : null;
  const targetCandidate = optionalFinite(input.targetRoas);
  const targetRoas = targetCandidate !== null && targetCandidate > 0 ? targetCandidate : null;

  const roas = input.adSpend > 0 ? (input.revenue / input.adSpend) * 100 : null;
  const revenuePerWon = input.adSpend > 0 ? input.revenue / input.adSpend : null;
  const adCostRate = input.revenue > 0 ? (input.adSpend / input.revenue) * 100 : null;

  const maxAdSpendBeforeLoss = marginRate === null
    ? null
    : input.revenue * (marginRate / 100);
  const estimatedProfitAfterAd = maxAdSpendBeforeLoss === null
    ? null
    : maxAdSpendBeforeLoss - input.adSpend;
  const breakEvenRoas = marginRate === null ? null : 100 / (marginRate / 100);

  const targetRevenue = targetRoas === null
    ? null
    : input.adSpend * (targetRoas / 100);
  const maxAdSpendAtTarget = targetRoas === null
    ? null
    : input.revenue / (targetRoas / 100);
  const revenueGapToTarget = targetRevenue === null ? null : targetRevenue - input.revenue;
  const adSpendHeadroomToTarget = maxAdSpendAtTarget === null
    ? null
    : maxAdSpendAtTarget - input.adSpend;

  return {
    adSpend: input.adSpend,
    revenue: input.revenue,
    roas,
    revenuePerWon,
    adCostRate,
    marginRate,
    targetRoas,
    estimatedProfitAfterAd,
    breakEvenRoas,
    maxAdSpendBeforeLoss,
    targetRevenue,
    maxAdSpendAtTarget,
    revenueGapToTarget,
    adSpendHeadroomToTarget,
  };
}

export function buildRoasScenarios(input: RoasDecisionInput): readonly RoasScenario[] {
  const scenarios: Array<{ id: string; label: string; description: string; input: RoasDecisionInput }> = [
    {
      id: 'current',
      label: '현재 조건',
      description: '입력한 광고비와 광고 매출 기준입니다.',
      input,
    },
    {
      id: 'revenue-down-10',
      label: '광고 매출 10% 감소',
      description: '광고비는 유지되고 광고 매출이 10% 줄어드는 경우입니다.',
      input: { ...input, revenue: input.revenue * 0.9 },
    },
    {
      id: 'ad-spend-up-10',
      label: '광고비 10% 증가',
      description: '광고 매출은 유지되고 광고비가 10% 늘어나는 경우입니다.',
      input: { ...input, adSpend: input.adSpend * 1.1 },
    },
    {
      id: 'combined-downside',
      label: '매출 -10% + 광고비 +10%',
      description: '광고 매출 감소와 광고비 증가가 동시에 발생하는 보수적 시나리오입니다.',
      input: {
        ...input,
        revenue: input.revenue * 0.9,
        adSpend: input.adSpend * 1.1,
      },
    },
  ];

  return scenarios.map((scenario) => ({
    id: scenario.id,
    label: scenario.label,
    description: scenario.description,
    result: calculateRoasDecision(scenario.input),
  }));
}

export function describeRoasDecision(result: RoasDecisionResult): string {
  const formatPercent = (value: number) => `${value.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
  const formatWon = (value: number) => `${Math.round(value).toLocaleString('ko-KR')}원`;

  if (result.roas === null) {
    return '광고비가 0원이라 ROAS를 계산할 수 없습니다.';
  }

  if (result.marginRate !== null && result.breakEvenRoas !== null && result.estimatedProfitAfterAd !== null) {
    const gap = result.roas - result.breakEvenRoas;
    if (result.estimatedProfitAfterAd > 0) {
      return `입력한 마진율 기준 광고비 차감 후 약 ${formatWon(result.estimatedProfitAfterAd)}의 이익이 남는 구조입니다. 현재 ROAS ${formatPercent(result.roas)}는 손익분기 ROAS ${formatPercent(result.breakEvenRoas)}보다 ${formatPercent(Math.abs(gap))}p 높습니다.`;
    }
    if (result.estimatedProfitAfterAd < 0) {
      return `입력한 마진율 기준 광고비 차감 후 약 ${formatWon(Math.abs(result.estimatedProfitAfterAd))}의 손실이 예상됩니다. 현재 ROAS ${formatPercent(result.roas)}는 손익분기 ROAS ${formatPercent(result.breakEvenRoas)}보다 ${formatPercent(Math.abs(gap))}p 낮습니다.`;
    }
    return `입력한 마진율 기준 현재 ROAS가 손익분기 수준입니다. 광고비를 차감한 추정 이익은 0원입니다.`;
  }

  if (result.targetRoas !== null) {
    const gap = result.roas - result.targetRoas;
    if (gap >= 0) {
      return `현재 ROAS ${formatPercent(result.roas)}는 목표 ROAS ${formatPercent(result.targetRoas)}를 ${formatPercent(gap)}p 상회합니다.`;
    }
    return `현재 ROAS ${formatPercent(result.roas)}는 목표 ROAS ${formatPercent(result.targetRoas)}보다 ${formatPercent(Math.abs(gap))}p 낮습니다.`;
  }

  return `현재 광고비 1원당 약 ${result.revenuePerWon?.toLocaleString('ko-KR', { maximumFractionDigits: 2 }) ?? '0'}원의 광고 매출이 발생한 것으로 계산됩니다.`;
}
